import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  Package,
  ImagePlus,
  Tag,
  Ruler,
  PencilLine,
  Trash2,
  AlertTriangle,
  Lock,
  Sparkles,
  ArrowUpRight,
  BookOpen,
  Hash,
  IndianRupee,
  Boxes,
  PackageX,
  FileText,
  Image as ImageIcon,
  BellRing,
  CheckCircle2,
  Search,
  Type,
  Eye,
  ExternalLink,
  Star,
  Filter,
  Download,
  Box,
  Images,
  UploadCloud,
  FileSpreadsheet,
} from "lucide-react";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";

/**
 * Plain-English, example-driven reference for the Catalog screen — what a
 * product card shows, what every field in the edit form does, how plan
 * limits and stock/sold-out behaviour work, and when low stock alerts fire.
 * Companion to TabsGuide.jsx / StatusGuide.jsx, same design system.
 */

const ACCENTS = {
  lilac: { chip: "bg-lilac", chipText: "text-ink", soft: "bg-lilac/25", ring: "ring-lilac" },
  mint: { chip: "bg-mint", chipText: "text-ink", soft: "bg-mint/25", ring: "ring-mint" },
  peach: { chip: "bg-peach", chipText: "text-ink", soft: "bg-peach/25", ring: "ring-peach" },
  hotpink: { chip: "bg-hotpink", chipText: "text-white", soft: "bg-hotpink/10", ring: "ring-hotpink" },
  ink: { chip: "bg-ink", chipText: "text-cream", soft: "bg-ink/[0.06]", ring: "ring-ink" },
};

const PLAN_LIMITS = [
  { plan: "Free", limit: "5 products" },
  { plan: "Creator", limit: "150 products" },
  { plan: "Starter", limit: "500 products" },
  { plan: "Growth", limit: "1,000 products" },
  { plan: "Pro", limit: "10,000 products" },
];

const SEARCH_FILTER_LIMITS = [
  { plan: "Free", limit: "1 filter at a time" },
  { plan: "Starter", limit: "2 filters at once" },
  { plan: "Growth", limit: "3 filters at once" },
  { plan: "Pro", limit: "4 filters at once" },
];

function FieldCard({ icon: Icon, label, desc, example, note, accent }) {
  const a = ACCENTS[accent];
  return (
    <div className="rounded-2xl border border-ink/10 bg-white p-4">
      <div className="flex items-center gap-2">
        <span className={`grid h-7 w-7 flex-none place-items-center rounded-lg ${a.chip} ${a.chipText}`}>
          <Icon size={13} strokeWidth={2.4} />
        </span>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-ink">{label}</p>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-ink/70">{desc}</p>
      {example && (
        <div className={`mt-2.5 rounded-xl ${a.soft} px-3 py-2`}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-ink/50">Example</p>
          <p className="mt-0.5 text-[12px] leading-relaxed text-ink/80">{example}</p>
        </div>
      )}
      {note && <p className="mt-2 text-[11px] leading-relaxed text-ink/45">{note}</p>}
    </div>
  );
}

function SectionActions({ actions, accent }) {
  if (!actions || actions.length === 0) return null;
  const a = ACCENTS[accent];
  return (
    <div className="mt-5 grid gap-3 sm:grid-cols-2">
      {actions.map((act) => (
        <div key={act.name} className={`rounded-2xl ${a.soft} p-4`}>
          <div className="flex items-center gap-2">
            {act.icon && <act.icon size={14} className="text-ink/60" />}
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-ink">{act.name}</p>
          </div>
          <p className="mt-1.5 text-[12px] leading-relaxed text-ink/70">{act.meaning}</p>
        </div>
      ))}
    </div>
  );
}

function GuideSection({ id, icon: Icon, accent, name, tagline, what, useFor, checkWhen, fields, actions, planNote, children }) {
  const a = ACCENTS[accent];
  return (
    <section id={id} className="scroll-mt-24 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3.5">
          <span className={`grid h-12 w-12 flex-none place-items-center rounded-2xl ${a.chip} ${a.chipText}`}>
            <Icon size={20} strokeWidth={2.2} />
          </span>
          <div>
            <p className={`text-xs font-bold uppercase tracking-[0.2em] ${accent === "hotpink" ? "text-hotpink" : "text-ink/50"}`}>
              {tagline}
            </p>
            <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">{name}</h2>
          </div>
        </div>
        {planNote && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-ink/[0.03] px-3 py-1.5 text-[11px] font-semibold text-ink/60">
            <Lock size={11} /> {planNote}
          </span>
        )}
      </div>

      {what && <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">{what}</p>}

      {(useFor || checkWhen) && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {useFor && (
            <div className="rounded-2xl bg-ink/[0.03] p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">Use it for</p>
              <p className="mt-1 text-[13px] leading-relaxed text-ink/70">{useFor}</p>
            </div>
          )}
          {checkWhen && (
            <div className="rounded-2xl bg-ink/[0.03] p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">Check it when</p>
              <p className="mt-1 text-[13px] leading-relaxed text-ink/70">{checkWhen}</p>
            </div>
          )}
        </div>
      )}

      {children}

      {fields && fields.length > 0 && (
        <div className="mt-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">What you'll see, field by field</p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {fields.map((f) => (
              <FieldCard key={f.label} accent={accent} {...f} />
            ))}
          </div>
        </div>
      )}

      <SectionActions actions={actions} accent={accent} />
    </section>
  );
}

export default function CatalogGuide() {
  const { me } = useOptionalMerchant();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Catalog Guide",
    description: "A field-by-field walkthrough of the Catalog screen — product cards, plan limits, stock, sizes, low stock alerts, and how in-DM product search works.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/catalog-guide",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Catalog Guide", item: "https://checkoutdm.com/catalog-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Catalog Guide | Checkout DM"
        description="A field-by-field walkthrough of the Catalog screen — product cards, plan limits, stock, sizes, low stock alerts, and how in-DM product search works."
        path="/catalog-guide"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">
          What the Catalog screen means
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/70 md:text-base">
          Your Catalog is what the bot actually shows customers in DMs — every
          product here is checkout-ready the moment you save it. This is a
          field-by-field walkthrough of product cards, the edit form, plan
          limits, stock, low stock alerts, and exactly how in-DM search
          matches (or misses) your products, with real examples.
        </p>
        <p className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
          <Link to="/tabs-guide" className="inline-flex items-center gap-1.5 font-semibold text-hotpink hover:underline">
            <BookOpen size={13} /> What each dashboard tab means →
          </Link>
          <Link to="/status-guide" className="inline-flex items-center gap-1.5 font-semibold text-hotpink hover:underline">
            <BookOpen size={13} /> What each status badge means →
          </Link>
        </p>

        {/* Quick nav */}
        <div className="mt-6 flex flex-wrap gap-2">
          {[
            { id: "overview", label: "Overview", icon: Package, accent: "lilac" },
            { id: "adding", label: "Adding products", icon: ImagePlus, accent: "mint" },
            { id: "card", label: "Product card", icon: Tag, accent: "peach" },
            { id: "editing", label: "Editing a product", icon: PencilLine, accent: "lilac" },
            { id: "bestseller", label: "Bestseller tagging", icon: Star, accent: "peach" },
            { id: "category", label: "Category, Gender & Color", icon: Filter, accent: "mint" },
            { id: "digital", label: "Digital products", icon: Download, accent: "lilac" },
            { id: "weight", label: "Weight & package size", icon: Box, accent: "peach" },
            { id: "links", label: "Preview & Buy links", icon: ExternalLink, accent: "mint" },
            { id: "gallery", label: "Extra gallery photos", icon: Images, accent: "lilac" },
            { id: "bulk", label: "Bulk tools", icon: UploadCloud, accent: "ink" },
            { id: "search", label: "How search works", icon: Search, accent: "mint" },
            { id: "search-tips", label: "Writing for search", icon: Type, accent: "lilac" },
            { id: "search-plans", label: "Search by plan", icon: Lock, accent: "ink" },
            { id: "stock", label: "Stock & sold out", icon: Boxes, accent: "peach" },
            { id: "alerts", label: "Low stock alerts", icon: BellRing, accent: "hotpink" },
            { id: "limits", label: "Plan limits", icon: Lock, accent: "ink" },
            { id: "deleting", label: "Deleting", icon: Trash2, accent: "hotpink" },
          ].map((t) => {
            const a = ACCENTS[t.accent];
            return (
              <a
                key={t.id}
                href={`#${t.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full ${a.chip} ${a.chipText} px-3.5 py-1.5 text-xs font-bold`}
              >
                <t.icon size={12} /> {t.label}
              </a>
            );
          })}
        </div>

        <div className="mt-8 space-y-6">
          {/* Overview */}
          <GuideSection
            id="overview"
            icon={Package}
            accent="lilac"
            name="Catalog"
            tagline="Your live storefront"
            what="Every product a customer can pick from when they DM your bot — photo, title, price, quantity, and sizes. There's no separate 'publish' step: the moment you save a product here, it's live in checkout."
            useFor="Building and maintaining the product list your bot shows to customers in DMs — this is the single source of truth for what's for sale."
            checkWhen="You're launching a new product, restocking, changing a price, or a customer says they can't find something in the bot's menu."
          />

          {/* Adding products */}
          <GuideSection
            id="adding"
            icon={ImagePlus}
            accent="mint"
            name="Adding a product"
            tagline="Photo first"
            what="You start by dropping in a photo — either via the dashed drop-zone or the 'Add products' button. That opens the edit form immediately in create mode, but nothing is saved to your catalog until you hit Save; the photo alone doesn't create a product."
            fields={[
              {
                icon: ImageIcon,
                label: "Photo requirements",
                desc: "JPG only, up to 1 MB per image. Other formats (PNG, WEBP, HEIC) or larger files are rejected before upload.",
                note: "If your phone saves photos as HEIC, convert or export as JPG first — the dashboard will reject anything else at the file picker.",
              },
              {
                icon: Lock,
                label: "When you're at your limit",
                desc: "If you've used every product slot on your plan, the drop-zone and 'Add products' button are replaced with an 'Upgrade plan' button — you can't add a new product until you either remove one or upgrade.",
              },
            ]}
          />

          {/* Product card */}
          <GuideSection
            id="card"
            icon={Tag}
            accent="peach"
            name="The product card"
            tagline="What you see in the grid"
            what="Every saved product shows as a card in the main grid — hover (or tap, on mobile) to reveal Edit and Delete."
            fields={[
              {
                icon: ImageIcon,
                label: "Photo",
                desc: "The product's main image, shown square-cropped. This is exactly what the customer sees in the bot's product menu.",
              },
              {
                icon: Tag,
                label: "Title & price",
                desc: "Product name and price, shown in your store's currency (₹ for India, $ elsewhere) exactly as the bot will quote it to a customer.",
              },
              {
                icon: Boxes,
                label: "Stock line",
                desc: "Shows '{n} left' if you're tracking quantity, 'unlimited' if you left quantity blank, or 'out of stock' in bold pink once it hits zero.",
                example: "\"12 left\" under a ₹899 hoodie means 12 units are trackable and will count down as orders come in.",
              },
              {
                icon: Ruler,
                label: "Size/stock preview",
                desc: "For products with sizes, a small line under the price lists each size with its remaining count (∞ for untracked sizes) — e.g. 'S: 4 · M: 0 · L: 9'.",
              },
              {
                icon: PackageX,
                label: "Sold Out badge",
                desc: "A pink 'Sold Out · Hidden from DMs' tag appears once stock hits zero. The product stays in your catalog and dashboard, but the bot stops offering it to customers until you restock.",
              },
              {
                icon: Lock,
                label: "Locked badge",
                desc: "A dark 'Locked · Upgrade to show' tag appears on products that exceed your plan's limit (see Plan limits below). The bot won't show these to customers either, even though they're not sold out.",
              },
            ]}
          />

          {/* Editing */}
          <GuideSection
            id="editing"
            icon={PencilLine}
            accent="lilac"
            name="Editing a product"
            tagline="The product form"
            what="Tap Edit on any card, or drop a new photo, to open the same form. Every field here maps directly to what the bot says and asks in a customer's DM."
            fields={[
              {
                icon: FileText,
                label: "Product title",
                desc: "Required. Up to 80 characters — this is exactly the name the bot uses when listing or confirming the product to a customer.",
              },
              {
                icon: IndianRupee,
                label: "Price",
                desc: "Required. A plain number in your store's currency — no need to type the ₹ or $ symbol.",
              },
              {
                icon: Boxes,
                label: "Quantity in stock",
                desc: "Only shown for products with no sizes. Leave it blank for unlimited/untracked stock, or type a number to have the bot count down as orders are placed and auto-mark it Sold Out at zero.",
              },
              {
                icon: Ruler,
                label: "Sizes",
                desc: "Optional, comma-separated (e.g. 'S, M, L, XL'). The moment you add sizes, the single Quantity field is replaced by a per-size stock grid below.",
              },
              {
                icon: Boxes,
                label: "Stock per size",
                desc: "Appears once you've typed sizes. Each size gets its own optional quantity box — blank means that size is untracked/unlimited, a number means the bot counts that size down independently.",
                example: "S left blank, M set to 6, L set to 0 → the bot offers S freely, offers M until 6 are sold, and won't offer L at all since it's already at zero.",
              },
              {
                icon: FileText,
                label: "Description",
                desc: "Optional, up to 200 characters. Fabric, fit, sizing notes, shipping info — whatever helps a customer decide without asking a follow-up question.",
              },
            ]}
          />

          {/* Bestseller tagging */}
          <GuideSection
            id="bestseller"
            icon={Star}
            accent="peach"
            name="Bestseller tagging"
            tagline="Pin your top sellers"
            planNote="Pro only"
            what="Tag up to 3 products as Bestsellers to pin them first — in your dashboard grid, on your public storefront, and at the front of the product carousel the bot sends customers in DMs."
            fields={[
              {
                icon: Star,
                label: "Tagging a product",
                desc: "Open Edit on a saved product and toggle Bestseller on. Capped at 3 across your whole catalog — tag a 4th and the toggle disables until you untag one first.",
              },
              {
                icon: Tag,
                label: "Where it shows",
                desc: "Bestseller products get a small ⭐ Bestseller badge on their card, and are pinned ahead of new arrivals and everything else, everywhere they appear.",
              },
            ]}
          />

          {/* Category, Gender & Color */}
          <GuideSection
            id="category"
            icon={Filter}
            accent="mint"
            name="Category, Gender & Color"
            tagline="Power DM filtering"
            planNote="Category: Creator and above · Gender & Color: Starter and above"
            what="Three optional fields that don't show up to customers as labels — they just power how precisely the bot can filter your catalog when a customer types or taps to browse."
            fields={[
              {
                icon: Tag,
                label: "Category",
                desc: "Free text, e.g. 'Tops', 'Bottoms', 'Accessories'. Powers the CATEGORIES command, letting a customer browse by category instead of scrolling your whole catalog.",
              },
              {
                icon: Filter,
                label: "Gender & Color",
                desc: "A Gender dropdown (Men / Women / Unisex / Kids) plus free-text Color. Both combine with keyword search — e.g. 'men black jeans' filters by gender and color at once.",
              },
            ]}
            note="How many of these a customer can combine in one search message depends on your plan — see Search limits by plan below."
          />

          {/* Digital products */}
          <GuideSection
            id="digital"
            icon={Download}
            accent="lilac"
            name="Digital products"
            tagline="No shipping, instant delivery"
            what="Switch a product from Physical to Digital in the edit form to sell things like e-books, templates, or course access instead of a physical item."
            fields={[
              {
                icon: Download,
                label: "Delivery link",
                desc: "Required once a product is set to Digital. Sent to the buyer automatically right after payment, both as an Instagram DM and by email.",
              },
              {
                icon: PackageX,
                label: "What disappears",
                desc: "Digital products hide Sizes, Weight, Package size, and the Buy link field entirely — none of those apply once there's nothing to ship.",
                note: "A Preview / sample link still works on Digital products — handy for a sample chapter or course trailer before someone buys.",
              },
            ]}
          />

          {/* Weight & package size */}
          <GuideSection
            id="weight"
            icon={Box}
            accent="peach"
            name="Weight & package size"
            tagline="Powers shipping rate calculation"
            what="Optional fields, physical products only — used to calculate accurate shipping rates through your connected courier. You don't have to fill these in for a product to sell; they only affect how shipping cost is calculated."
            fields={[
              {
                icon: Box,
                label: "Weight (grams)",
                desc: "Optional. Used together with package size to estimate shipping cost — leave it blank if you're not sure yet.",
              },
              {
                icon: Ruler,
                label: "Package size (L × W × H, cm)",
                desc: "Optional, in centimetres. Any side left blank defaults to 10cm for that dimension — so leaving all three blank means shipping is calculated as if the package were 10×10×10cm.",
                example: "Leave Length and Width blank but set Height to 20 → shipping is calculated as a 10×10×20cm package.",
              },
            ]}
          />

          {/* Preview & Buy links */}
          <GuideSection
            id="links"
            icon={ExternalLink}
            accent="mint"
            name="Preview & Buy links"
            tagline="One extra button, your choice"
            what="Every product can carry Select plus, if you want, exactly one more button — either a Preview link or a Buy link. You choose which one fits the product; you can't add both at the same time."
            useFor="Giving a customer more context before they buy (Preview), or sending them straight to an external link you earn from instead of checking out in the DM (Buy link)."
            fields={[
              {
                icon: Eye,
                label: "Sample / demo link (Preview)",
                desc: "Optional, on every plan including Free. Adds a \"👁 Preview\" button next to Select that opens your link when tapped.",
                example: "An e-book sample chapter, a course trailer, or a short demo video — anything that helps a customer decide before buying.",
              },
              {
                icon: ExternalLink,
                label: "Buy link (affiliate / external)",
                desc: "Optional, Starter+ and physical products only. Adds a button with your own custom label (up to 20 characters, defaults to \"🛒 Shop now\") that sends the customer to your link instead of DM checkout.",
                example: "Use it for anything you don't fulfil yourself — an Amazon affiliate link, a brand's own store, or any external checkout you earn a commission from.",
                note: "Not available on Free, and not shown for Digital products — those use their own Delivery link instead.",
              },
            ]}
          >
            <div className="mt-4 rounded-2xl bg-hotpink/5 p-4">
              <p className="text-[13px] leading-relaxed text-ink/70">
                <strong>Only one at a time.</strong> Typing a Buy link automatically clears any Preview
                link on that product, and typing a Preview link clears any Buy link — so a product card
                never shows more than two buttons total: <strong>Select</strong>, plus either Preview or
                Buy, never both.
              </p>
            </div>
          </GuideSection>

          {/* Extra gallery photos */}
          <GuideSection
            id="gallery"
            icon={Images}
            accent="lilac"
            name="Extra gallery photos"
            tagline="Storefront only"
            planNote="Pro only"
            what="Add up to 3 additional photos per product, shown in the gallery on your public storefront page. DM checkout always uses only the main photo — this doesn't change that."
            fields={[
              {
                icon: ImageIcon,
                label: "Adding a photo",
                desc: "In the edit form, tap any of the 3 empty slots below the main photo to upload — same JPG-only, 1 MB limit as the main photo.",
              },
              {
                icon: PackageX,
                label: "Removing one",
                desc: "Hover (or tap) a filled slot and hit the × to clear it — you can replace or remove any of the 3 slots independently without touching the others.",
              },
            ]}
          />

          {/* Bulk tools */}
          <GuideSection
            id="bulk"
            icon={UploadCloud}
            accent="ink"
            name="Bulk tools"
            tagline="Export & bulk upload"
            planNote="Pro only"
            what="Two toolbar buttons at the top of the Catalog page for handling many products at once, instead of one at a time."
            fields={[
              {
                icon: FileSpreadsheet,
                label: "Export CSV",
                desc: "Downloads your entire catalog as a CSV file — useful for backups or bulk edits outside the dashboard.",
              },
              {
                icon: UploadCloud,
                label: "Bulk upload photos",
                desc: "Upload up to 50 JPG photos at once. Each becomes a new draft product titled 'Untitled product — tap Edit to finish', with no price set yet — you still need to open each one and fill in the details before it goes live.",
              },
            ]}
            note="Both buttons are visible on every plan, but tapping them below Pro takes you to the Upgrade page instead of opening the tool — so everyone can see what they're missing."
          />

          {/* How search works */}
          <GuideSection
            id="search"
            icon={Search}
            accent="mint"
            name="How search works"
            tagline="What the bot actually matches"
            what="When a customer types something instead of tapping through your menu — 'red kurti under 500', 'do you have size M' — the bot searches before falling back to showing your full catalog. Here's exactly what it checks, so you know why a search did or didn't find a product."
            useFor="Understanding why a specific search returned nothing, or returned the wrong products, so you can fix the listing instead of guessing."
            checkWhen="A customer says they searched for something and it didn't show up."
            fields={[
              {
                icon: FileText,
                label: "What actually gets searched",
                desc: "Your product's Title and Description, combined and matched case-insensitively. Nothing else — not the photo, not the size list, not any other field.",
                note: "If a word isn't in the Title or Description, no amount of it being 'obvious from the photo' will make the product show up.",
              },
              {
                icon: Type,
                label: "Words, not exact phrases",
                desc: "Your message is broken into individual words. Filler words ('a', 'the', 'looking for', 'do you have', 'please') are stripped out automatically, and every remaining word has to appear somewhere in the Title + Description — it's an AND, not an OR.",
                example: "\"I'm looking for a red kurti please\" → filler dropped → the bot searches for products containing both \"red\" AND \"kurti\".",
              },
              {
                icon: Ruler,
                label: "Sizes are structured data, not text",
                desc: "A size only matches if it's an actual size on that product in your Sizes/Stock fields. Writing \"available in M, L, XL\" inside the Description does nothing for search.",
                note: "If a size search comes up empty, check the product's real Sizes field first — not the description.",
              },
              {
                icon: IndianRupee,
                label: "Price ranges, in plain language",
                desc: "Recognized automatically and applied fully — with no limit, on every plan, unlike keyword search below.",
                example: "\"under 500\", \"below 1k\", \"above 2000\", \"500 to 1000\", and \"between 500 and 1000\" are all understood.",
              },
              {
                icon: Tag,
                label: "The \"shirt\" vs \"T-Shirt\" rule",
                desc: "Searching \"shirt\" alone deliberately excludes products that only match because they contain \"T-Shirt\" — treated as a separate category. \"tshirt\", \"t-shirt\", and \"t shirt\" are all read as the same word and match your tees normally.",
                example: "A product named just \"T-Shirt\" won't appear for a \"shirt\" search, but will for a \"tshirt\" search. A product named \"Formal Shirt\" appears for \"shirt\" either way.",
                note: "If you sell both, name tees explicitly \"T-Shirt\" (not just \"Shirt\") so the two searches don't collide.",
              },
              {
                icon: Type,
                label: "Simple plurals are matched automatically",
                desc: "You don't need both forms in your listing.",
                example: "\"shirts\" and \"shirt\" match the same products. \"dresses\" and \"dress\" do too.",
              },
            ]}
          />

          {/* Writing for search */}
          <GuideSection
            id="search-tips"
            icon={Type}
            accent="lilac"
            name="Writing listings that get found"
            tagline="Title + Description decide everything"
            what="Since search only ever looks at your Title and Description, how you write them directly decides what a customer can find by typing."
            fields={[
              {
                icon: Tag,
                label: "Say it the way a customer would type it",
                desc: "Work the exact words a customer is likely to search — product type, color, material, style — into the Title and/or Description. A beautifully-written but vague description can be invisible to search.",
                example: "Title \"Red Cotton Kurti — Straight Fit\" gets found by searches for \"red\", \"cotton\", and \"kurti\". Title \"Ethnic Wear #4\" gets found by none of them.",
              },
              {
                icon: Search,
                label: "Cover alternate names, even ones you wouldn't use",
                desc: "Search only matches literal words — it doesn't know synonyms. If a style commonly goes by more than one name, mention both.",
                example: "Some customers will type \"kurta\" where you'd say \"kurti\" — mention both if there's any ambiguity. Same for \"hoodie\" vs \"sweatshirt\".",
              },
              {
                icon: FileText,
                label: "Keep it readable — customers read this too",
                desc: "Your Description shows up in the bot's replies exactly as written. Stuffing it with every possible keyword makes it worse to read without matching any better beyond covering each relevant word once.",
                note: "One accurate sentence with the right words beats a comma-separated keyword dump, for search and for the customer reading it.",
              },
              {
                icon: Ruler,
                label: "Sizes go in the Sizes field — always",
                desc: "The most common way size search silently breaks: sizes typed only into the Description, never added to the actual Sizes/Stock fields. A human reads the sentence fine; the bot can't search it as a size at all.",
              },
            ]}
          >
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-hotpink/20 bg-hotpink/5 p-4">
                <p className="text-[11px] font-bold uppercase tracking-wide text-hotpink">Weak listing</p>
                <p className="mt-1.5 text-sm font-semibold text-ink">"Nice Top"</p>
                <p className="mt-1 text-[13px] text-ink/60">"Good quality, comfy, everyone loves it."</p>
                <p className="mt-2.5 text-[11px] font-semibold text-ink/50">Found by a "red kurti" search? No.</p>
              </div>
              <div className="rounded-2xl border border-mint/40 bg-mint/15 p-4">
                <p className="text-[11px] font-bold uppercase tracking-wide text-ink/60">Strong listing</p>
                <p className="mt-1.5 text-sm font-semibold text-ink">"Red Cotton Kurti — Straight Fit"</p>
                <p className="mt-1 text-[13px] text-ink/60">"Breathable cotton kurti (kurta) in red, straight fit, daily wear."</p>
                <p className="mt-2.5 text-[11px] font-semibold text-ink/50">Found by a "red kurti" search? Yes.</p>
              </div>
            </div>
          </GuideSection>

          {/* Search by plan */}
          <GuideSection
            id="search-plans"
            icon={Lock}
            accent="ink"
            name="Search limits by plan"
            tagline="Same rules, different room"
            what="Matching itself works identically on every plan — same tokenizing, same price parsing. What changes by plan is how many things (keyword words + a size) the bot will combine from a single message."
          >
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {SEARCH_FILTER_LIMITS.map((p) => (
                <div key={p.plan} className="rounded-2xl bg-ink/[0.03] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-ink/45">{p.plan}</p>
                  <p className="mt-1 text-sm font-semibold text-ink">{p.limit}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-2xl bg-hotpink/5 p-4">
              <p className="text-[13px] leading-relaxed text-ink/70">
                Price ranges ("under 500") are <strong>never limited by this</strong> — they always apply
                fully, on every plan. This limit only covers keyword words (like "red", "kurti") plus a
                size, combined into one search.
              </p>
            </div>
            <div className="mt-3 rounded-2xl bg-ink/[0.03] p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">Same search, by plan</p>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ink/70">
                A customer types <strong>"red kurti size M"</strong>:
              </p>
              <ul className="mt-2 space-y-1.5 text-[13px] leading-relaxed text-ink/70">
                <li><strong>Free (1):</strong> size M applied, "red" and "kurti" both dropped — matches any product in size M.</li>
                <li><strong>Starter (2):</strong> size M + "red" applied, "kurti" dropped.</li>
                <li><strong>Growth (3):</strong> size M + "red" + "kurti" — the full search applied.</li>
                <li><strong>Pro (4):</strong> same as Growth here, with headroom for an even longer search.</li>
              </ul>
              <p className="mt-2.5 text-[11px] leading-relaxed text-ink/45">
                Keyword words are taken in the order the customer typed them, so the earlier words in a
                search matter more on lower plans.
              </p>
            </div>
            <div className="mt-3 rounded-2xl bg-ink/[0.03] p-4">
              <p className="text-[13px] leading-relaxed text-ink/70">
                On Free specifically, this rarely matters in practice — with a 5-product cap, most
                customers just see your whole catalog directly rather than needing to search at all.
                Search becomes genuinely useful as your catalog grows past what's easy to browse.
              </p>
            </div>
          </GuideSection>

          {/* Stock & sold out */}
          <GuideSection
            id="stock"
            icon={Boxes}
            accent="peach"
            name="Stock & sold out behaviour"
            tagline="How counts actually work"
            what="Stock tracking is entirely optional, per product and per size — you choose whether CheckoutDM manages it for you or you manage it yourself outside the platform."
            fields={[
              {
                icon: Boxes,
                label: "Blank = unlimited",
                desc: "Leaving quantity (or a size's stock) blank tells the bot never to worry about running out — it will keep offering that item indefinitely, no matter how many orders come in.",
              },
              {
                icon: PackageX,
                label: "A number = tracked",
                desc: "Typing any number, including 0, switches that product or size into tracked mode. The count automatically drops by the ordered quantity every time a customer completes checkout, and the item is auto-hidden from the bot once it reaches zero.",
              },
              {
                icon: CheckCircle2,
                label: "Restocking",
                desc: "To bring a sold-out item back, just edit it and type a new quantity greater than zero — it becomes available to customers again immediately, no separate 'republish' step.",
              },
            ]}
          />

          {/* Low stock alerts */}
          <GuideSection
            id="alerts"
            icon={BellRing}
            accent="hotpink"
            name="Low stock alerts"
            tagline="Growth & Pro only"
            planNote="Growth & Pro only"
            what="A per-product (or per-size) threshold that gets you a dashboard notification before you actually run out, instead of finding out only when a product goes Sold Out."
            fields={[
              {
                icon: BellRing,
                label: "Setting a threshold",
                desc: "In the edit form, 'Low stock alert at' (or the per-size version, when the product has sizes) lets you type the count at which you want to be warned. Leave it blank for no alert.",
                example: "Setting the alert at 5 for a product with 20 in stock means you're notified the moment it drops to 5 or fewer — you still have runway to reorder before it hits zero.",
              },
              {
                icon: Lock,
                label: "Plan requirement",
                desc: "This field only appears for Growth and Pro merchants. On Free or Starter, stock still counts down and still auto-hides at zero — you just won't get an early warning notification before that happens.",
              },
            ]}
          />

          {/* Plan limits */}
          <GuideSection id="limits" icon={Lock} accent="ink" name="Plan limits" tagline="How many products you can list">
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {PLAN_LIMITS.map((p) => (
                <div key={p.plan} className="rounded-2xl bg-ink/[0.03] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-ink/45">{p.plan}</p>
                  <p className="mt-1 text-sm font-semibold text-ink">{p.limit}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-2xl bg-hotpink/5 p-4">
              <p className="text-[13px] leading-relaxed text-ink/70">
                The usage bar at the top of Catalog always shows your live{" "}
                <strong>used / limit</strong> count. Once you're at your cap, adding new
                products is blocked until you remove one or upgrade — the button changes
                to <strong>Upgrade plan</strong> automatically.
              </p>
            </div>
            <div className="mt-3 rounded-2xl bg-ink/[0.03] p-4">
              <p className="text-[13px] leading-relaxed text-ink/70">
                If you downgrade while carrying more products than your new plan
                allows, nothing is deleted — but your newest products beyond the new
                limit switch to <strong>Locked</strong> and stop showing to customers
                until you upgrade again or remove enough products to fit.
              </p>
            </div>
          </GuideSection>

          {/* Deleting */}
          <GuideSection
            id="deleting"
            icon={Trash2}
            accent="hotpink"
            name="Deleting a product"
            tagline="Permanent, with a confirm step"
            what="Tap Delete on a card and confirm — this removes the product from your catalog and from any active DM checkout link immediately."
            fields={[
              {
                icon: AlertTriangle,
                label: "This can't be undone",
                desc: "There's no archive or trash — once you confirm, the product is gone from your catalog for good. If a customer is mid-checkout with that product when you delete it, the bot will let them know it's no longer available.",
              },
              {
                icon: Boxes,
                label: "Frees up a plan slot",
                desc: "Deleting a product immediately frees up one slot against your plan limit, so you can add a new one right away even if you were previously at your cap.",
              },
            ]}
          />
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[2rem] border border-hotpink/20 bg-hotpink/5 p-6 md:p-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Still stuck?</p>
            <p className="mt-1 max-w-md text-sm text-ink/70">
              If something on your Catalog screen still doesn't make sense after reading this, reach out and we'll add it here for the next person too.
            </p>
          </div>
          <a
            href="/plans"
            className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
          >
            See plans <ArrowUpRight size={14} />
          </a>
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}
